/**
 * Склейка страниц истории чата.
 *
 * Лента собирается из нескольких запросов: последняя страница при открытии,
 * более ранние — при прокрутке вверх, и снова последняя — при перечитывании
 * после отправки или правки. Склеивать их надо так, чтобы:
 *
 *  • не было дублей: страницы TDLib перекрываются на граничном сообщении, а
 *    одно и то же сообщение приходит то с нашим UUID, то с `tg:N`;
 *  • перечитывание не выбрасывало уже подгруженное раньше: иначе после
 *    каждой отправки лента схлопывалась бы обратно до последних 50.
 */

import type { TelegramMessageDto } from "@tw/shared-types";
import { telegramIdOf } from "./message-id.ts";

type Message = Pick<TelegramMessageDto, "id" | "telegramMessageId" | "sentAt">;

/** Ключ сообщения: номер в Telegram, а без него — наш id. */
function keyOf(message: Message): string {
  return telegramIdOf(message) ?? message.id;
}

/** Раньше ли `a`, чем `b`. По времени, при равенстве — по номеру Telegram. */
function isOlder(a: Message, b: Message): boolean {
  const ta = a.sentAt ? Date.parse(a.sentAt) : 0;
  const tb = b.sentAt ? Date.parse(b.sentAt) : 0;
  if (ta !== tb) return ta < tb;
  const na = telegramIdOf(a);
  const nb = telegramIdOf(b);
  if (na === null || nb === null) return false;
  return BigInt(na) < BigInt(nb);
}

/** Ранняя страница — сверху; уже показанные не дублируются. */
export function prependOlder<T extends Message>(current: T[], older: T[]): T[] {
  const known = new Set(current.map(keyOf));
  const fresh = older.filter((m) => !known.has(keyOf(m)));
  return fresh.length === 0 ? current : [...fresh, ...current];
}

/**
 * Перечитанная последняя страница поверх ленты.
 *
 * Всё, что раньше первого сообщения страницы, остаётся как было; остальное
 * заменяется страницей — в ней свежие правки, реакции и отметки прочтения.
 */
export function mergeLatest<T extends Message>(current: T[], latest: T[]): T[] {
  const first = latest[0];
  if (!first) return current.length === 0 ? latest : current;

  const inPage = new Set(latest.map(keyOf));
  const earlier = current.filter((m) => !inPage.has(keyOf(m)) && isOlder(m, first));
  return earlier.length === 0 ? latest : [...earlier, ...latest];
}
